import { CommonModule, NgFor } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { AppManagementService } from '../../Utils/_services/app-management/app-management.service';
import { BugReportingService } from '../../Utils/_services/bug-report/bug-reporting.service';
import { IBugReportingEntityDto } from '../../feature/admin/content/bug-reporting/bug-report.model';
import { ToastrService } from 'ngx-toastr';
import { ICustomError } from '../../Utils/_models/network-response';
import { TooltipDirective } from '../../Utils/_directive/tooltip.directive';
import { IBugFixLog_BugTypes } from './bug-fix-log.model';
import { NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { IAppManagementEntityDto } from '../../Utils/_services/app-management/app-management.model';

@Component({
  selector: 'app-bug-fix-log',
  standalone: true,
  imports: [NgFor, CommonModule, CardModule, DialogModule,
    FormsModule, SelectModule, ButtonModule, TooltipDirective, NgbCollapse],
  templateUrl: './bug-fix-log.component.html',
  styleUrl: './bug-fix-log.component.css'
})
export class BugFixLogComponent {

  showCreateDialog = false;
  showRequestDialog = false;

  bugs: IBugReportingEntityDto[] = [];
  paginatedBugs: any[] = [];
  allBugs: IBugReportingEntityDto[] = [];
  totalBugs: number = 0;
  currentPage: number = 1;
  itemsPerPage: number = 6;
  totalPages: number = 0;
  fixedBugsTotal: number = 0;
  openBugsTotal: number = 0;
  inProgressBugsTotal: number = 0;
  isLoading: boolean = false;

  apps: IAppManagementEntityDto[] = [];
  priorityOpts: Array<string> = ["low", "medium", "high"];
  selectedPriority: string | null = null;
  selectedApp: string | null = null;
  newBugDescription: string = '';
  userComment: string = '';
  attachedFiles: Array<{File: File, FileName: string}> = [];
  selectedBug: {Name: string, Description: string} | null = null;
  bugTypes: Array<IBugFixLog_BugTypes> = [
    {
      Name: "Functional Bugs",
      Description: "Features not working as expected (e.g., broken buttons, form submission failures)"
    },
    {
      Name: "App Crashes/Freezes",
      Description: "The app closes unexpectedly or becomes completely unresponsive."
    },
    {
      Name: "UI/UX Issues",
      Description: "Visual errors, such as overlapping text, truncated labels, or images not loading."
    },
    {
      Name: "Performance Issues",
      Description: "Slow loading times, high battery consumption, or poor network responsiveness."
    },
    {
      Name: "Compatibility Bugs",
      Description: "Issues specific to certain devices, screen sizes, or OS versions (e.g., Android 15 vs. iOS)."
    },
    {
      Name: "Data/Logic Errors",
      Description: "Incorrect calculations, wrong data displayed, or failed synchronization."
    },
    {
      Name: "Notification Errors",
      Description: "Push notifications not appearing, delayed, or containing incorrect information."
    },
    {
      Name: "Security Vulnerabilities",
      Description: "Data leaks or issues with user authentication."
    },
    {
      Name: "Installation/Update Failures",
      Description: "Issues downloading, installing, or updating the app from the store"
    },
    {
      Name: "Other",
      Description: "Bug type not listed above"
    }
  ]

  constructor(private appService: AppManagementService,
    private bugService: BugReportingService,
    private toastr: ToastrService
  ) { }


  ngOnInit(): void {
    this.loadBugs();
  }

  loadApps(): void {
    this.appService.getAllApps().subscribe({
      next: (response) => {
        this.apps = response.data;
      },
      error: (err: ICustomError) => {
        console.error('Failed to load all apps:', err.error);
        this.toastr.error('Error loading apps. Please try again.', 'Error');
      }
    });
  }


  loadBugs(): void {
    this.isLoading = true;
    this.bugService.getAllBugs(this.currentPage, this.itemsPerPage).subscribe({
      next: (response) => {
        this.bugs = response.data.items || [];
        this.totalBugs = response.data.totalCount || 0;
        this.totalPages = Math.ceil(this.totalBugs / this.itemsPerPage);
        this.fixedBugsTotal = response.data.totalClosed ?? this.getFixedBugs(this.bugs);
        this.inProgressBugsTotal = response.data.totalInProgress ?? this.getInProgressBugs(this.bugs);
        this.openBugsTotal = response.data.totalOpen ?? this.getOpenBugs(this.bugs);
        this.updatePaginatedBugs();
        this.isLoading = false;
      },
      error: (err: ICustomError) => {
        console.error('Failed to load bugs:', err.error);
        this.toastr.error('Error loading bugs. Please try again.', 'Error');
        this.isLoading = false;
      }
    });
  }

  clearCached(): void {
    this.bugService.clearCached().subscribe({
      next: (response) => {
        this.loadBugs();
      },
      error: (err: ICustomError) => {
        console.log("Failed to clear cached");
        this.toastr.error("Error clearing cached, Please try again");
      }
    })
  }

  updatePaginatedBugs(): void {
    this.paginatedBugs = [...this.bugs];
  }

  openCreateDialog() {
    this.loadApps();
    this.showCreateDialog = true;
  }

  openRequestDialog() {
    this.showRequestDialog = true;
  }

  onUploadAttachments(event: any): void {
    this.attachedFiles = [];
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      // Iterate over the FileList and append each file to the FormData object
      for (let i = 0; i < fileList.length; i++) {
        this.attachedFiles.push({File: fileList[i], FileName: fileList[i].name});
      }
    }
  }

  submitNewBug() {
    if (!this.selectedApp || !this.selectedBug|| !this.newBugDescription || !this.selectedPriority) {
      this.toastr.warning('Please fill in all required fields.', 'Validation Error');
      return;
    }

    const bugForm = new FormData();
    bugForm?.append("appId", this.selectedApp);
    bugForm?.append("title", this.selectedBug.Name);
    bugForm?.append("description", this.newBugDescription);
    bugForm?.append("priority", this.selectedPriority);

    if (this.attachedFiles.length) {
      for (let i: number = 0; i < this.attachedFiles.length; i++) {
        bugForm.append("attachments", this.attachedFiles[i].File, this.attachedFiles[i].FileName);
      }
    }

    this.bugService.create(bugForm).subscribe({
      next: (createdBug) => {
        this.clearCached();
        this.toastr.success('Bug report created successfully!', 'Success');

        this.selectedApp = null;
        this.selectedPriority = null;
        this.selectedBug = null;
        this.newBugDescription = '';
        this.showCreateDialog = false;
        // Don't forget to clear attachedFiles;
        this.attachedFiles = [];
      },
      error: (err: ICustomError) => {
        console.error('Failed to create bug:', err.error);
        this.toastr.error('Error creating bug. Please try again.', 'Error');
      }
    });
  }

  getTotalBugs(): number {
    return this.totalBugs || this.allBugs.length;
  }

  private getFixedBugs(allBugs: Array<IBugReportingEntityDto>): number {
    return allBugs.filter(b =>
      b.status?.toLowerCase() === 'closed' ||
      b.status?.toLowerCase() === 'completed' ||
      b.status?.toLowerCase() === 'resolved'
    ).length;
  }

  private getInProgressBugs(allBugs: Array<IBugReportingEntityDto>): number {
    return allBugs.filter(b =>
      b.status?.toLowerCase().includes('progress') ||
      b.status?.toLowerCase() === 'in-progress' ||
      b.status?.toLowerCase() === 'working'
    ).length;
  }

  private getOpenBugs(allBugs: Array<IBugReportingEntityDto>): number {
    return allBugs.filter(b =>
      b.status?.toLowerCase() === 'open' ||
      b.status?.toLowerCase() === 'new' ||
      b.status?.toLowerCase() === 'pending' ||
      !b.status
    ).length;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadBugs();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadBugs();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadBugs();
    }
  }

  getPaginationArray(): number[] {
    const maxVisible = 5;
    const start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(this.totalPages, start + maxVisible - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getStatusIcon(status: string): string {
    const normalizedStatus = status.trim().toLowerCase();
    switch (normalizedStatus) {
      case 'fixed':
      case 'completed':
      case 'resolved':
        return '✅';
      case 'in-progress':
      case 'progress':
      case 'working':
        return '🔄';
      default:
        return '🔴';
    }
  }

  downloadFile(singeBug: IBugReportingEntityDto) {
      this.bugService.download({
        FileName: singeBug.attachments ? singeBug.attachments[0].fileName : "",
        FileSize: singeBug.attachments ? singeBug.attachments[0].fileSize : 0,
        ContentType: singeBug.attachments ? singeBug.attachments[0].contentType : ""
      }).subscribe({
      next: (createdBug) => {
      // const link = this.renderer.createElement('a');
      // link.setAttribute('target', '_blank');
      // link.setAttribute('href', createdBug.data);
      // link.setAttribute('download', `products.png`);
      // link.click();
      // link.remove();
      },
      error: (err) => {
          console.error('Failed to download file:', err.error);
          this.toastr.error('Error downloading file. Please try again.', 'Error');
      }});
   }
}
